import React from 'react';
import { Card } from '../common/Card';
import { ResponsiveHeading } from '../common/ResponsiveHeading';

/**
 * ErrorLayout Component
 * 
 * Shared layout for all error pages with consistent styling and structure.
 * Provides a professional, user-friendly error display following industry standards.
 * 
 * Industry Pattern: GitHub 404, Vercel errors, Stripe error pages
 * 
 * Features:
 * - Centered card layout
 * - Consistent spacing and typography
 * - Dark mode compatible
 * - Mobile-first responsive
 * - Accessible (semantic HTML, ARIA labels)
 * 
 * @component
 * @example
 * ```tsx
 * <ErrorLayout
 *   title="404 - Page Not Found"
 *   description="The page you're looking for doesn't exist."
 *   icon={<FileQuestion className="w-16 h-16" />}
 *   actions={
 *     <>
 *       <Button variant="primary" onClick={() => navigate('/')}>
 *         Go Home
 *       </Button>
 *       <Button variant="secondary" onClick={() => navigate(-1)}>
 *         Go Back
 *       </Button>
 *     </>
 *   }
 * />
 * ```
 * 
 * @param {ErrorLayoutProps} props - Component props
 * @param {string} props.title - Error title (e.g., "404 - Page Not Found")
 * @param {string} props.description - Error description for users
 * @param {React.ReactNode} props.icon - Error icon (Lucide icon recommended)
 * @param {React.ReactNode} props.actions - Action buttons (usually navigation)
 * @param {React.ReactNode} [props.children] - Additional content (optional)
 * @param {string} [props.className] - Additional CSS classes
 * 
 * @returns {JSX.Element} Rendered error layout
 */
export interface ErrorLayoutProps {
  /** Error title */
  title: string;
  /** Error description */
  description: string;
  /** Error icon */
  icon: React.ReactNode;
  /** Action buttons */
  actions?: React.ReactNode;
  /** Additional content */
  children?: React.ReactNode;
  /** Additional CSS classes */
  className?: string;
}

export const ErrorLayout: React.FC<ErrorLayoutProps> = ({
  title,
  description,
  icon,
  actions,
  children,
  className = '',
}) => {
  return (
    <div className={`min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-900 p-4 ${className}`}>
      <Card 
        padding="lg" 
        shadow="lg" 
        className="max-w-md w-full text-center"
      >
        {/* Icon */}
        <div className="flex justify-center mb-6">
          <div className="p-4 bg-gray-100 dark:bg-slate-800 rounded-full text-gray-600 dark:text-slate-400">
            {icon}
          </div>
        </div>

        {/* Title */}
        <ResponsiveHeading 
          level={1} 
          className="text-gray-900 dark:text-slate-100 font-bold mb-3"
        >
          {title}
        </ResponsiveHeading>

        {/* Description */}
        <p className="text-gray-600 dark:text-slate-400 text-base mb-6">
          {description}
        </p>

        {/* Additional Content */}
        {children && (
          <div className="mb-6">
            {children}
          </div>
        )}

        {/* Actions */}
        {actions && (
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            {actions}
          </div>
        )}
      </Card>
    </div>
  );
};

